import { useEffect, useMemo, useState } from 'react'
import { FaTrashAlt, FaUserPlus } from 'react-icons/fa'
import { BeatLoader } from 'react-spinners'
import Button from '../../components/atoms/Buttons'
import { createToast } from '../../utils/Toast'
import { useUserContext } from '../../context/UserContext'
import {
  getAllFolderGrants,
  getFolderGrants,
  listFiles,
  updateFolderGrants,
  formatFileDate,
} from '../../utils/fileBrowserApi'

const Toast = createToast()

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const INTERNAL_DOMAIN = '@saddlefit.io'

/**
 * Settings-level overview of external file sharing: everyone outside
 * SaddleFit with access to a private folder, plus a quick way to share a
 * folder with new emails. Per-folder management (including SaddleFit team
 * read/write) stays in File Browser → Manage Access.
 */
export default function FileSharingCard() {
  const { isSaddlefitAdmin } = useUserContext()

  const [grants, setGrants] = useState([])
  const [folders, setFolders] = useState([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(null)

  const [emailsInput, setEmailsInput] = useState('')
  const [prefix, setPrefix] = useState('')
  const [access, setAccess] = useState('write')
  const [saving, setSaving] = useState(false)
  const [busyGrant, setBusyGrant] = useState(null) // `${email}|${prefix}` being toggled/removed

  const busy = saving || Boolean(busyGrant)

  const load = async () => {
    setLoading(true)
    setLoadError(null)
    try {
      // Folder list first — the share form must work even if the grants
      // overview fails.
      const listRes = await listFiles({ bucket: 'private', prefix: '' })
      const folderList = listRes.folders || []
      setFolders(folderList)

      let grantList
      try {
        grantList = (await getAllFolderGrants()).grants || []
      } catch (err) {
        // Backend without the all=true mode deployed yet — aggregate the
        // per-folder grant lists for the top-level folders instead (misses
        // grants on nested folders until the backend is updated).
        console.warn('all-grants unavailable, falling back per folder:', err?.message)
        const perFolder = await Promise.all(
          folderList.map(f =>
            getFolderGrants(f.prefix)
              .then(res => (res.grants || []).map(g => ({ prefix: f.prefix, ...g })))
              .catch(() => []),
          ),
        )
        grantList = perFolder.flat()
      }

      // External shares only — internal admin ACLs and personal drop folders
      // are managed elsewhere.
      setGrants(grantList.filter(g => !g.internal && !g.personal))
    } catch (err) {
      console.error('File sharing load failed:', err)
      setLoadError(err.message || 'Failed to load file sharing overview')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isSaddlefitAdmin) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaddlefitAdmin])

  // Folder choices: top-level private folders plus any deeper prefixes that
  // already carry a grant (so existing nested shares stay selectable).
  const folderOptions = useMemo(() => {
    const options = new Set(folders.map(f => f.prefix))
    for (const g of grants) options.add(g.prefix)
    return [...options].sort()
  }, [folders, grants])

  const emails = useMemo(
    () =>
      [...new Set(
        emailsInput
          .split(/[\s,;]+/)
          .map(e => e.trim().toLowerCase())
          .filter(Boolean),
      )],
    [emailsInput],
  )
  const invalidEmails = emails.filter(e => !EMAIL_RE.test(e))
  const internalEmails = emails.filter(e => e.endsWith(INTERNAL_DOMAIN))
  const canAdd =
    emails.length > 0 && !invalidEmails.length && !internalEmails.length && prefix && !busy

  const handleAdd = async () => {
    if (!canAdd) return
    setSaving(true)
    try {
      const res = await updateFolderGrants({ prefix, add: emails, access })
      const parts = []
      if (res.added?.length) parts.push(`${res.added.length} granted access`)
      if (res.invited?.length) parts.push(`${res.invited.length} invited by email`)
      if (res.errors?.length) parts.push(`${res.errors.length} failed`)
      Toast.fire({
        icon: res.errors?.length ? 'warning' : 'success',
        title: parts.join(', ') || 'No changes',
      })
      if (res.errors?.length) console.error('Grant errors:', res.errors)
      setEmailsInput('')
      await load()
    } catch (err) {
      console.error('Grant failed:', err)
      Toast.fire({ icon: 'error', title: err.message || 'Failed to grant access' })
    } finally {
      setSaving(false)
    }
  }

  const handleAccess = async (grant, level) => {
    if (busy || grant.access === level) return
    setBusyGrant(`${grant.email}|${grant.prefix}`)
    try {
      const res = await updateFolderGrants({ prefix: grant.prefix, add: [grant.email], access: level })
      if (res.errors?.length) throw new Error(res.errors[0].message)
      Toast.fire({ icon: 'success', title: `${grant.email} set to ${level} access` })
      await load()
    } catch (err) {
      console.error('Access update failed:', err)
      Toast.fire({ icon: 'error', title: err.message || 'Failed to update access' })
    } finally {
      setBusyGrant(null)
    }
  }

  const handleRemove = async grant => {
    setBusyGrant(`${grant.email}|${grant.prefix}`)
    try {
      await updateFolderGrants({ prefix: grant.prefix, remove: [grant.email] })
      Toast.fire({ icon: 'success', title: `Access removed for ${grant.email}` })
      await load()
    } catch (err) {
      console.error('Revoke failed:', err)
      Toast.fire({ icon: 'error', title: err.message || 'Failed to remove access' })
    } finally {
      setBusyGrant(null)
    }
  }

  if (!isSaddlefitAdmin) return null

  return (
    <div className="mt-6 bg-white border border-[#EAEBED] rounded-[12px] p-6 font-montserrat">
      <h2 className="text-[18px] font-bold text-[#2b364b] mb-1">File Sharing</h2>
      <p className="text-[12px] text-[#6b7280] m-0 mb-4 max-w-[560px]">
        Share private folders with people outside SaddleFit. They sign in on the
        files portal; <strong>Write</strong> access also lets them upload, delete,
        create subfolders and invite others there. SaddleFit team access is managed
        per folder in File Browser → Manage Access.
      </p>

      <label className="block text-[12px] text-[#2b364b] uppercase tracking-wide mb-1">
        Share a folder
      </label>
      <div className="flex gap-2 flex-wrap items-center">
        <select
          value={prefix}
          onChange={e => setPrefix(e.target.value)}
          disabled={busy || loading}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#2b364b] max-w-[220px]"
        >
          <option value="">Select folder…</option>
          {folderOptions.map(p => (
            <option key={p} value={p}>
              {p}
            </option>
          ))}
        </select>
        <input
          type="text"
          value={emailsInput}
          onChange={e => setEmailsInput(e.target.value)}
          placeholder="partner@example.com, another@example.com"
          disabled={busy}
          className="flex-1 min-w-[220px] border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2b364b]"
        />
        <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
          {['read', 'write'].map(level => (
            <button
              key={level}
              type="button"
              disabled={busy}
              onClick={() => setAccess(level)}
              title={level === 'read' ? 'View and download only' : 'Also upload, delete, new folders and invites'}
              className={`px-3 py-2 text-xs font-medium border-none capitalize ${
                access === level
                  ? 'bg-[#2b364b] text-white cursor-default'
                  : 'bg-white text-gray-500 hover:text-[#2b364b] cursor-pointer'
              }`}
            >
              {level}
            </button>
          ))}
        </div>
        <Button
          type="button"
          primary
          AppendIcon={FaUserPlus}
          loading={saving}
          disabled={!canAdd}
          onClick={handleAdd}
          className="create-saddle-btn !w-auto px-5 cursor-pointer"
        >
          Add
        </Button>
      </div>
      {invalidEmails.length > 0 && (
        <p className="text-red-500 text-xs mt-1 mb-0">Invalid: {invalidEmails.join(', ')}</p>
      )}
      {internalEmails.length > 0 && (
        <p className="text-amber-600 text-xs mt-1 mb-0">
          {internalEmails.join(', ')} — {INTERNAL_DOMAIN} accounts already have access;
          manage team read/write in File Browser → Manage Access.
        </p>
      )}
      <p className="text-xs text-gray-400 mt-1 mb-0">
        People without an account will be emailed an invitation to create one.
      </p>

      <div className="mt-5">
        <h3 className="text-[14px] font-bold text-[#2b364b] m-0 mb-2">
          People with access
        </h3>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <BeatLoader color="#2b364b" size={8} />
          </div>
        ) : loadError ? (
          <p className="text-sm text-red-500 m-0 py-2">{loadError}</p>
        ) : grants.length === 0 ? (
          <p className="text-sm text-gray-500 m-0 py-2">
            No one outside SaddleFit has file access yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ borderSpacing: 0 }}>
              <thead>
                <tr className="text-left text-gray-500">
                  <th className="py-2 font-medium">Email</th>
                  <th className="py-2 font-medium">Folder</th>
                  <th className="py-2 font-medium">Since</th>
                  <th className="py-2 font-medium">Access</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {grants.map(grant => {
                  const key = `${grant.email}|${grant.prefix}`
                  return (
                    <tr key={key} className="border-t border-gray-100">
                      <td className="py-2 pr-2 break-all">{grant.email}</td>
                      <td className="py-2 pr-2 break-all text-gray-500">{grant.prefix}</td>
                      <td className="py-2 pr-2 whitespace-nowrap text-gray-500">
                        {grant.grantedAt ? formatFileDate(grant.grantedAt) : '—'}
                      </td>
                      <td className="py-2 pr-2">
                        {busyGrant === key ? (
                          <BeatLoader color="#2b364b" size={5} />
                        ) : (
                          <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
                            {['read', 'write'].map(level => (
                              <button
                                key={level}
                                type="button"
                                disabled={busy}
                                onClick={() => handleAccess(grant, level)}
                                className={`px-3 py-1 text-xs font-medium border-none capitalize ${
                                  grant.access === level
                                    ? 'bg-[#2b364b] text-white cursor-default'
                                    : 'bg-white text-gray-500 hover:text-[#2b364b] cursor-pointer'
                                }`}
                              >
                                {level}
                              </button>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="py-2" align="right">
                        {busyGrant !== key && (
                          <button
                            type="button"
                            title="Remove access"
                            disabled={busy}
                            className="text-gray-500 hover:text-red-600 bg-transparent border-none cursor-pointer p-0"
                            onClick={() => handleRemove(grant)}
                          >
                            <FaTrashAlt size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
